import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import { pluralise, pluraliseS } from 'lib/pluralise';
import { View } from 'react-native';
import { ButtonToggleGroup } from 'components/ButtonToggleGroup';
import {
  EngageFlatIsHMO,
  EngageFlatIsCommerciallyOwned,
} from 'types/DTOs/BlockManagerDTOs';
import {
  filterArrayEmpty,
  formatDate,
  isoDateStringToDate,
  rbAddReferencePrefix,
} from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  doEngageFlatChangeOccupationStatus,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import {
  EngageFlatContact,
  FlatOccupationStatus,
  ResidentSurveyStatusData,
} from 'types/Engage';
import { getFormattedFlatName } from 'store/remote/engageUtils';
import { EngageFlatModalCommunicationsPage } from './EngageFlatModalCommunicationsPage';
import { EngageContactCard } from './Components/EngageContactCard';
import { UI_DATE_FORMAT_LONG } from 'lib/constants';
import { selectEngageFlat } from 'store/remote/engageSelectors';
import {
  doEngageArchiveContact,
  doEngageFlatArchivedContactsLoad,
  doEngageFlatChangeIsHMO,
  doEngageFlatChangeIsCommerciallyOwned,
  doEngageUnarchiveContact,
} from 'store/remote/engageActions';
import { getFlatIssues } from 'lib/engage/getFlatIssues';

export const EngageFlatModal: React.FC<
  ModalScreenProps<ModalScreens.EngageFlat>
> = ({ route }) => {
  const dispatch = useDispatch();

  const { key } = route.params;

  const [activeTab, setActiveTab] = React.useState<'information' | 'messages'>(
    'information',
  );

  const handleClose = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch]);

  useEsc(handleClose);

  const {
    flat: { name: flatName },
  } = useSelector((state) => selectEngageFlat(state, key))!;

  const NavigationHeaderMemo = React.useMemo(
    () => (
      <NavigationHeader
        heading={getFormattedFlatName(flatName)}
        cancelButtonText="Close"
        onPressCancelButton={handleClose}
        menuRight={{
          iconButtonConfig: {
            icon: ICONS.EllipsisLight,
          },
          options: [
            {
              text: 'Send Message',
              onPress: () =>
                dispatch(
                  NAVIGATE_MODAL({
                    screen: ModalScreens.EngageSendCommunication,
                    params: { key, contactId: null },
                  }),
                ),
            },
          ],
        }}
      />
    ),
    [dispatch, flatName, handleClose, key],
  );

  const ButtonToggleGroupMemo = React.useCallback(
    (thisActiveTab: 'information' | 'messages') => (
      <ButtonToggleGroup
        key={thisActiveTab}
        rounded
        buttons={[
          { text: 'Information', key: 'information' },
          { text: 'Messages', key: 'messages' },
        ]}
        active={thisActiveTab}
        onActiveChange={(newActive) => {
          setTimeout(() => setActiveTab(newActive), 1);
          return false;
        }}
        containerStyle={{
          marginBottom: theme.paddingVerticalHalf,
          alignSelf: 'stretch',
          marginHorizontal: theme.paddingHorizontal,
        }}
        size="small"
      />
    ),
    [],
  );

  return (
    <>
      <View
        style={[
          activeTab === 'information' ? { flex: 1 } : { display: 'none' },
        ]}
      >
        <FlatModalInformationTab
          pageKey={key}
          HeaderItems={
            <>
              {NavigationHeaderMemo}
              {ButtonToggleGroupMemo('information')}
            </>
          }
        />
      </View>
      <View
        style={[activeTab === 'messages' ? { flex: 1 } : { display: 'none' }]}
      >
        <EngageFlatModalCommunicationsPage
          pageKey={key}
          contactId={null}
          HeaderItems={
            <>
              {NavigationHeaderMemo}
              {ButtonToggleGroupMemo('messages')}
            </>
          }
        />
      </View>
    </>
  );
};

const FlatModalInformationTab: React.FC<{
  pageKey: string;
  HeaderItems: React.ReactNode;
}> = ({ pageKey: key, HeaderItems }) => {
  const dispatch = useDispatch();
  const { property, flat, contacts, residentSurvey, archivedContacts } =
    useSelector((state) => selectEngageFlat(state, key))!;

  // const hasResident = contacts.some((c) => c.isResident);
  const residentsCount = contacts.filter((c) => c.isResident).length;

  const issues = getFlatIssues(flat);

  const handleOpenContact = (contact: EngageFlatContact) => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.EngageContact,
        params: { key, contact },
      }),
    );
  };

  const handleOpenCreateContact = () => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.EngageContact,
        params: { key, contact: null },
      }),
    );
  };

  const handleUnarchiveContact = React.useCallback(
    (contactId: string) => {
      dispatch(
        doEngageUnarchiveContact({
          key,
          propertyId: property.id,
          flatId: flat.id,
          contactId,
        }),
      );
    },
    [dispatch, flat.id, key, property.id],
  );

  return (
    <PageScrollView isInModal={true} header={HeaderItems}>
      <Card>
        <CardRow
          labelAlignTop
          label={getFormattedFlatName(flat.name)}
          value={[
            property.name,
            property.parentName,
            ...property.addressLines,
            property.postcode,
          ]
            .filter(filterArrayEmpty)
            .join(', ')}
        />
        <ViewWithSeparators horizontal>
          <CardRow
            value={rbAddReferencePrefix(flat.reference)}
            icon={ICONS.RBPlectrumSolid}
          />
          <CardRow
            value={flat.customReference}
            style={{ flex: 1 }}
            icon={ICONS.HashtagLight}
          />
        </ViewWithSeparators>
        {!!issues.length &&
          issues.map((issue) => (
            <CardRow
              key={issue.key}
              label={issue.label}
              labelColor={theme.colors.red}
              icon={ICONS.DiamondExclamationSolid}
              iconColor={theme.colors.red}
              value={issue.message}
              valueColor={theme.colors.red}
              labelAlignTop
            />
          ))}
      </Card>

      <Card explanation="Notes are for internal use only and are not visible to contacts">
        <ViewWithSeparators horizontal>
          <CardRow
            label="HMO"
            labelAlignTop
            style={{ flex: 1 }}
            onPress={() => dispatch(doEngageFlatChangeIsHMO({ key }))}
            value={
              flat.isHMO === EngageFlatIsHMO.YES
                ? 'Yes'
                : flat.isHMO === EngageFlatIsHMO.NO
                  ? 'No'
                  : 'Not Known'
            }
          />
        </ViewWithSeparators>
        <CardRow
          label="Commercially Owned"
          labelAlignTop
          style={{ flex: 1 }}
          onPress={() =>
            dispatch(doEngageFlatChangeIsCommerciallyOwned({ key }))
          }
          value={
            flat.isCommerciallyOwned === EngageFlatIsCommerciallyOwned.YES
              ? 'Yes'
              : flat.isCommerciallyOwned === EngageFlatIsCommerciallyOwned.NO
                ? 'No'
                : 'Not Known'
          }
        />
        <CardRow label="Internal Notes" placeholder="No Notes" labelAlignTop />
      </Card>

      <Card label="Resident Survey">
        {residentSurvey ? (
          <CardRow
            label={ResidentSurveyStatusData[residentSurvey.status].name}
            value={
              (residentSurvey.endDate ? 'Valid Between ' : 'Valid From ') +
              formatDate(
                isoDateStringToDate(residentSurvey.startDate),
                UI_DATE_FORMAT_LONG,
              ) +
              (residentSurvey.endDate
                ? ` – ` +
                  formatDate(
                    isoDateStringToDate(residentSurvey.endDate),
                    UI_DATE_FORMAT_LONG,
                  )
                : '')
            }
            labelAlignTop
          />
        ) : (
          <CardRow label={'Not Set'} />
        )}
      </Card>

      <Card
        label={pluralise(
          contacts.length,
          '1 Contact',
          '{x} Contacts',
          'Contacts',
        )}
        labelLinkText="Add Contact"
        onLabelLinkPress={handleOpenCreateContact}
        marginBottomQuarter
      >
        <CardRow
          onPress={() => dispatch(doEngageFlatChangeOccupationStatus({ key }))}
          label="Occupation Status"
          value={
            flat.occupationStatus === FlatOccupationStatus.OCCUPIED
              ? residentsCount
                ? `Occupied (${pluraliseS(residentsCount, 'Resident')})`
                : 'Occupied (Residents Not Known)'
              : flat.occupationStatus === FlatOccupationStatus.UNOCCUPIED
                ? 'Unoccupied'
                : 'Not Known'
          }
          valueColor={
            flat.occupationStatus === FlatOccupationStatus.OCCUPIED &&
            !residentsCount
              ? theme.colors.red
              : undefined
          }
        />
      </Card>

      <Card marginBottomQuarter={archivedContacts === null}>
        {!contacts.length && <CardRow value="No Contacts" />}
        {contacts.map((contact) => (
          <EngageContactCard
            key={contact.id}
            contact={contact}
            showAddedInfo
            showArchivedInfo
            onPress={handleOpenContact}
            menuOptions={[
              {
                text: 'Open Contact',
                onPress: () => handleOpenContact(contact),
              },
              {
                text: 'Archive Contact',
                onPress: () =>
                  dispatch(
                    doEngageArchiveContact({
                      key,
                      propertyId: property.id,
                      flatId: flat.id,
                      contactId: contact.id,
                    }),
                  ),
                icon: ICONS.TrashCanLight,
                destructive: true,
              },
            ]}
          />
        ))}
      </Card>

      {archivedContacts === null && (
        <Card>
          <CardRow
            label={'See Archived Contacts'}
            labelColor={theme.colors.blue}
            onPress={() =>
              dispatch(
                doEngageFlatArchivedContactsLoad({
                  key,
                  propertyId: property.id,
                  flatId: flat.id,
                }),
              )
            }
          />
        </Card>
      )}

      {archivedContacts !== null && (
        <Card
          label={pluralise(
            archivedContacts.length,
            '1 Archived Contact',
            '{x} Archived Contacts',
            'No Archived Contacts',
          )}
        >
          {!archivedContacts.length && <CardRow value="No Archived Contacts" />}
          {archivedContacts.map((contact) => (
            <EngageContactCard
              key={contact.id}
              contact={contact}
              showAddedInfo
              showArchivedInfo
              menuOptions={[
                {
                  text: 'Unarchive Contact',
                  onPress: () => handleUnarchiveContact(contact.id),
                },
              ]}
            />
          ))}
        </Card>
      )}
    </PageScrollView>
  );
};
